/**
 * Shared single-flight refresh-on-401 interceptor (iter-156sf-step2).
 *
 * Why this module exists: the app has TWO axios surfaces — the global
 * `axios` default instance (legacy feature code) AND the centralized
 * `apiClient` (`axios.create()`, used by the report form + photo
 * upload, i.e. the exact flow where field techs got bounced to login
 * mid-walk when their 60-min access token expired). The refresh
 * interceptor used to live only on the global instance, so `apiClient`
 * 401s were never refreshed.
 *
 * Installing the interceptor here, with ONE module-level lock shared
 * across every instance, guarantees that simultaneous 401s on
 * different instances trigger exactly ONE `/auth/refresh` call; all
 * other in-flight requests park and retry on their ORIGINATING
 * instance once the rotation completes.
 *
 * Behaviour preserved from the original AuthContext interceptor:
 *   • 402 → redirect to /billing/reactivate (subscription canceled).
 *   • 401 (not /auth/*, not already retried) → single-flight refresh,
 *     sending the stored refresh token via `X-Refresh-Token` (Safari
 *     cross-site cookie fallback), then retry the original request.
 *   • Grace-race 401 from /auth/refresh (backend detail contains
 *     "raced") → brief wait + retry, NO redirect.
 *   • Any other refresh failure → clearTokens + redirect to /login.
 *   • Non-auth rejections tagged `_silenceOverlay` so fire-and-forget
 *     callers don't trip the React dev overlay.
 */
import { getRefreshToken, setTokens, clearTokens } from '@/lib/authToken';
import { signOutForReason } from '@/lib/loginNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared across every axios instance this module is installed on.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  failedQueue = [];
};

/**
 * Install the refresh-on-401 response interceptor on a given axios
 * instance. `refreshClient` is the axios instance used to CALL
 * /auth/refresh (defaults to the same instance); `instance` is what
 * the original failed request is retried on.
 */
export function installRefreshInterceptor(instance, refreshClient = instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const url = originalRequest?.url || '';

      // The Driver Portal has its own token lifecycle + its own login
      // page (/driver/login). It must NEVER be bounced to the dispatcher
      // /login or /billing pages by this shared interceptor. On iPhone/
      // Safari, a first-visit `/driver/me` 401 (no session yet) was
      // triggering a failed dispatcher /auth/refresh → redirect to
      // /login ~1s after arriving. Let driver 401s surface to the
      // Driver Portal caller, which handles them itself.
      const isDriver = url.includes('/driver/');

      // 402 Payment Required → subscription canceled. Route to the
      // reactivate page once (avoid looping if already there).
      if (
        !isDriver &&
        error.response?.status === 402 &&
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/billing/')
      ) {
        window.location.href = '/billing/reactivate';
        return Promise.reject(error);
      }

      if (
        !isDriver &&
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !url.includes('/auth/')
      ) {
        // Mark so a post-rotation 401 on the same request can't loop
        // back through the refresh path.
        originalRequest._retry = true;

        if (isRefreshing) {
          // Park: retry on THIS instance once the in-flight refresh
          // (possibly started by another instance) resolves.
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: () => resolve(instance(originalRequest)),
              reject,
            });
          });
        }

        isRefreshing = true;
        try {
          const rt = getRefreshToken();
          const { data: refreshData } = await refreshClient.post(
            `${API}/auth/refresh`,
            {},
            { withCredentials: true, headers: rt ? { 'X-Refresh-Token': rt } : {} },
          );
          setTokens(refreshData);
          processQueue(null);
          return instance(originalRequest);
        } catch (refreshError) {
          const detail = refreshError?.response?.data?.detail || '';
          const isGraceRace =
            typeof detail === 'string' && detail.toLowerCase().includes('raced');
          if (isGraceRace) {
            // Winning tab already rotated the cookies; wait a tick for
            // them to land, then retry without redirecting.
            await new Promise((r) => setTimeout(r, 150));
            processQueue(null);
            isRefreshing = false;
            return instance(originalRequest);
          }
          processQueue(refreshError);
          // e.g. "sign in again with a login code": handled once per page (the first request redirects)
          const reason = (await signOutForReason(refreshError)) || (await signOutForReason(error));
          clearTokens();
          if (reason !== 'again' && typeof window !== 'undefined') window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (error?.isAxiosError) {
        error._silenceOverlay = true;
      }
      return Promise.reject(error);
    },
  );
}
